import type { GridSlotProps } from '@mui/x-data-grid';
import { Autocomplete, Chip, TextField } from '@mui/material';

/**
 * Material UI v7-compatible replacement for the Data Grid's `baseAutocomplete`
 * slot.
 *
 * `@mui/x-data-grid@9`'s stock Material adapter is written against Material UI
 * v9: its `renderInput` reads `params.slotProps.htmlInput`, and it renders tags
 * through the v9-only `renderValue` prop. On Material UI v7 `renderInput`
 * receives `{ InputProps, inputProps, InputLabelProps }` and no `slotProps`, so
 * the stock adapter throws `Cannot read properties of undefined (reading
 * 'htmlInput')` and takes the whole page down.
 *
 * The slot has exactly two consumers — the "is any of" filter inputs — so
 * overriding it is enough to keep filtering working until the apps move to
 * Material UI v9. Delete this file at that point.
 */
export function GridBaseAutocomplete(props: GridSlotProps['baseAutocomplete']): React.ReactElement {
  const { getOptionLabel, label, placeholder, slotProps, ...other } = props;
  const { slotProps: textFieldSlotProps, ...textFieldProps } = slotProps?.textField ?? {};

  return (
    <Autocomplete
      {...other}
      getOptionLabel={getOptionLabel}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              variant="outlined"
              size="small"
              label={typeof option === 'string' ? option : getOptionLabel?.(option)}
              {...tagProps}
            />
          );
        })
      }
      renderInput={({ inputProps, InputProps, InputLabelProps, ...inputRest }) => (
        <TextField
          {...inputRest}
          label={label}
          placeholder={placeholder}
          {...textFieldProps}
          slotProps={{
            htmlInput: { ...inputProps, ...textFieldSlotProps?.htmlInput },
            input: { ...InputProps, ...textFieldSlotProps?.input },
            inputLabel: { ...InputLabelProps, shrink: true, ...textFieldSlotProps?.inputLabel },
          }}
        />
      )}
    />
  );
}
